/* eslint-disable check-preloaders/no-promise-all */
import { expValEquals } from '@confluence/feature-experiments';
import { getStreamInject } from '@confluence/ssr-data-streaming/entry-points/server.ts';

import { _resetLoadableGlobalsOnlyForTest } from './loadableGlobals.ts';
import * as preloaderV1 from './preloaderV1.ts';
import * as preloaderV2 from './preloaderV2.ts';

declare global {
	interface Window {
		__PRELOADER_V2__?: boolean;
	}
}

// Loadable modules call preloadIfNeeded before Statsig initialises, so the client cannot read the
// experiment itself. SSR evaluates confluence_ssr_react_lazy_preload_v2 and writes the answer next to
// __LOADABLE__, which keeps every call in one boot on the same version.
const getPreloader = () => (window.__PRELOADER_V2__ ? preloaderV2 : preloaderV1);

export function preloadLoadable(): Promise<any> {
	return getPreloader().preloadLoadable();
}

export function preloadIfNeeded(
	id: string | undefined,
	name: string | undefined,
	preload: () => Promise<any>,
): void {
	getPreloader().preloadIfNeeded(id, name, preload);
}

const loadableSet = new Set<string>();
const emptyLoadableSet = new Set<string>();
const nonEmptyLoadableSet = new Set<string>();
const injectionDelays = new Set<Promise<void>>();
let getStreamedLoadablePreload: ((loadableId: string) => string) | null = null;

function waitForArbitraryDelay(delay: number) {
	/**
	 * React render -> ReactOutputTransform -> inject loadable id
	 * We need to inject loadable id after ReactOutputTransform has processed the React rendering result.
	 * However there is no easy way for it. Currently we just delay the injection for a short period of time.
	 * Hopefully by the time the delay is over. We should be able to tell if a component has produced any content.
	 *
	 * This approach will make injections to happen after render is complete.
	 * waitForInjectionsToComplete() ensures we don't have missing injections.
	 */
	const promise = new Promise<void>((resolve) => {
		setTimeout(() => {
			injectionDelays.delete(promise);
			resolve();
		}, delay);
	});
	injectionDelays.add(promise);
	return promise;
}

export function waitForInjectionsToComplete(): Promise<void[]> {
	return Promise.all(Array.from(injectionDelays));
}

export function addLoadableId(id?: string): void {
	if (!id) return;

	if (window?.__SSR_REACT_STREAMING__) {
		// A streamed loadable only learns whether it rendered content after the transform has seen it
		void waitForArbitraryDelay(5).then(() => {
			if (isEmptyLoadableId(id) || loadableSet.has(id)) {
				return;
			}
			loadableSet.add(id);
			const preload = getStreamedLoadablePreload;
			if (preload && expValEquals('confluence_ssr_streamed_loadable_preload', 'isEnabled', true)) {
				// Built when the stream drains it, so files the head has written by then are skipped
				getStreamInject()(() => preload(id));
			}
		});
	} else {
		loadableSet.add(id);
	}
}

/**
 * Set by the SSR render: returns the inline script that starts downloading a late streamed
 * loadable's split files, or an empty string when there is nothing to preload.
 */
export function setStreamedLoadablePreload(
	streamedLoadablePreload: ((loadableId: string) => string) | null,
): void {
	getStreamedLoadablePreload = streamedLoadablePreload;
}

export function addEmptyLoadableId(id?: string): void {
	if (!id) {
		return;
	}
	emptyLoadableSet.add(id);
}

export function addNonEmptyLoadableId(id?: string): void {
	if (!id) return;
	nonEmptyLoadableSet.add(id);
}

export function getLoadableIds(): string[] {
	return Array.from(loadableSet);
}

export function getEmptyLoadableIds(): string[] {
	return Array.from(emptyLoadableSet).filter((id) => !nonEmptyLoadableSet.has(id));
}

export function isEmptyLoadableId(id?: string): boolean {
	if (!id) return false;
	return emptyLoadableSet.has(id) && !nonEmptyLoadableSet.has(id);
}

export function _resetOnlyForTest(): void {
	_resetLoadableGlobalsOnlyForTest();
	loadableSet.clear();
	getStreamedLoadablePreload = null;
	emptyLoadableSet.clear();
	nonEmptyLoadableSet.clear();
	preloaderV1._resetPreloaderV1OnlyForTest();
	preloaderV2._resetPreloaderV2OnlyForTest();
}
